import { useEffect, useState } from "react"
import Modal from "Components/molecule/modal"
import ActionButton from "Components/atoms/buttons/action-button"
import TextInput from "Components/molecule/inputs/text-input"
import SearchSelect from "Components/molecule/inputs/search-select"
import { AccountOption, CodeDescription, CostCodePayload, ExpenseBudget, ExpenseBudgetCostCode } from "models/expense-budget"
import { getExpenseBudgetAccountOptions, getExpenseBudgetProjectOptions } from "slices/finance/expense-budget/thunk"
import { formatCurrency } from "utils/tools"

interface CostCodeFormModalProps {
    isOpen: boolean
    budget: ExpenseBudget
    costCode: ExpenseBudgetCostCode | null
    isSaving: boolean
    onClose: () => void
    onSave: (payload: CostCodePayload) => Promise<void>
}

const projectLabel = (project: CodeDescription) => `${project.code.trim()} — ${project.description}`
const accountLabel = (account: AccountOption) =>
    `${account.code.trim()} — ${account.description} (${formatCurrency(account.amount, "CAD", 0)})`

// Pick the project first, then one of the GL accounts the department posted on it this year.
const CostCodeFormModal = ({ isOpen, budget, costCode, isSaving, onClose, onSave }: CostCodeFormModalProps) => {
    const [projects, setProjects] = useState<CodeDescription[]>([])
    const [accounts, setAccounts] = useState<AccountOption[]>([])
    const [projectId, setProjectId] = useState("")
    const [accountId, setAccountId] = useState("")
    const [approvedAmount, setApprovedAmount] = useState("0")
    const [isLoadingProjects, setIsLoadingProjects] = useState(false)
    const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        setProjectId(costCode?.projectId ?? "")
        setAccountId(costCode?.transactionAccountId ?? "")
        setApprovedAmount(costCode?.approvedAmount ?? "0")
        setIsLoadingProjects(true)
        getExpenseBudgetProjectOptions(budget.id).then(result => {
            setProjects(result ?? [])
            setIsLoadingProjects(false)
        })
    }, [isOpen, costCode, budget.id])

    useEffect(() => {
        setAccounts([])
        setIsLoadingAccounts(false)
        if (!projectId) return
        // Ignore a slow response for a project that is no longer selected.
        let isCurrent = true
        setIsLoadingAccounts(true)
        getExpenseBudgetAccountOptions(budget.id, projectId).then(result => {
            if (!isCurrent) return
            setAccounts(result ?? [])
            setIsLoadingAccounts(false)
        })
        return () => { isCurrent = false }
    }, [projectId, budget.id])

    const handleChangeProject = (value: string) => {
        setProjectId(value)
        setAccountId("")
    }

    // Keep the saved account selectable even if it has no postings this year.
    const savedAccount = costCode && !accounts.some(a => a.id === costCode.transactionAccountId)
        ? [{ ...costCode.transactionAccount, amount: 0 }] : []
    const accountOptions = [...savedAccount, ...accounts].map(a => ({ value: a.id, label: accountLabel(a) }))
    const projectOptions = projects.map(p => ({ value: p.id, label: projectLabel(p) }))

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={costCode ? "Edit Cost Code" : "New Cost Code"}
            subtitle={`Actuals = GL postings on this project and account by ${budget.department.description} and its sub-departments.`}>
            <div className="d-flex flex-column gap-2">
                <SearchSelect label="Project" placeholder={isLoadingProjects ? "Loading projects..." : "Type to search (e.g. X)"}
                    options={projectOptions} isLoading={isLoadingProjects}
                    value={projectId} onChange={option => handleChangeProject(option?.value ?? "")} />
                <SearchSelect label="GL Account"
                    placeholder={!projectId ? "Choose a project first" : isLoadingAccounts ? "Loading accounts..." : "Choose an account"}
                    options={accountOptions} isLoading={isLoadingAccounts}
                    value={accountId} onChange={option => setAccountId(option?.value ?? "")} />
                {projectId && !isLoadingAccounts && accounts.length === 0 &&
                    <span className="text-muted">No GL postings by this department on this project in FY{budget.fiscalYearId}.</span>}
                <TextInput label="Approved amount (CAD)" type="number" min={0} value={approvedAmount}
                    onChange={e => setApprovedAmount(e.target.value)} />
                <div className="d-flex justify-content-end gap-2">
                    <ActionButton onClick={onClose}>Cancel</ActionButton>
                    <ActionButton disabled={!projectId || !accountId || isSaving}
                        onClick={() => onSave({ transactionAccountId: accountId, projectId, approvedAmount: Number(approvedAmount) })}>
                        {isSaving ? "Saving..." : "Save"}
                    </ActionButton>
                </div>
            </div>
        </Modal>
    )
}

export default CostCodeFormModal
